import { useState, useEffect, useRef } from 'react';
import api from '../api';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'employee';
}

// Global state to prevent multiple verification calls
let globalUser: User | null = null;
let globalLoading = true;
let verificationInProgress = false;
let verificationPromise: Promise<User | null> | null = null;

const verifyTokenOnce = async (): Promise<User | null> => {
  if (verificationInProgress && verificationPromise) {
    console.log('Auth: Verification already in progress, waiting...');
    return verificationPromise;
  }

  const token = localStorage.getItem('token');
  
  if (!token) {
    console.log('Auth: No token found');
    globalUser = null;
    globalLoading = false;
    return null;
  }

  console.log('Auth: Starting token verification...');
  verificationInProgress = true;
  verificationPromise = (async () => {
    try {
      const response = await api.get('/auth/me');
      
      if (response.data.success) {
        const userData = response.data.user;
        console.log('Auth: Token verification successful', userData.username);
        globalUser = userData;
        return userData;
      } else {
        console.log('Auth: Token verification failed - invalid response');
        // Token is invalid, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        globalUser = null;
        return null;
      }
    } catch (error) {
      console.error('Auth: Token verification error:', error);
      // Token is invalid or expired, clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      globalUser = null;
      return null;
    } finally {
      verificationInProgress = false;
      verificationPromise = null;
      globalLoading = false;
    }
  })();

  return verificationPromise;
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(globalUser);
  const [loading, setLoading] = useState(globalLoading);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const initializeAuth = async () => {
      // If we already have a user and we're not loading, don't verify again
      if (globalUser && !globalLoading && hasInitialized.current) {
        console.log('Auth: Using cached user data, skipping verification');
        setUser(globalUser);
        setLoading(false);
        return;
      }

      // If verification is already in progress, wait for it
      if (verificationInProgress && verificationPromise) {
        console.log('Auth: Verification in progress, waiting for result...');
        try {
          const result = await verificationPromise;
          setUser(result);
          setLoading(false);
        } catch (error) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      // Perform verification
      console.log('Auth: Initializing authentication...');
      try {
        const result = await verifyTokenOnce();
        setUser(result);
        setLoading(false);
        hasInitialized.current = true;
      } catch (error) {
        setUser(null);
        setLoading(false);
        hasInitialized.current = true;
      }
    };

    initializeAuth();
  }, []);

  const login = (token: string, userData: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    globalUser = userData;
    globalLoading = false;
    setUser(userData);
    setLoading(false);
    hasInitialized.current = true;
    // Reset verification state
    verificationInProgress = false;
    verificationPromise = null;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    globalUser = null;
    globalLoading = true;
    setUser(null);
    setLoading(false);
    hasInitialized.current = false;
    // Reset verification state
    verificationInProgress = false;
    verificationPromise = null;
  };

  const updateUser = (userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    globalUser = userData;
    setUser(userData);
  };

  return {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };
}; 